import { Check, ChevronDown, Plus, Users } from 'lucide-react'
import { useEffect, useRef } from 'react'
import useBracketStore from '@/stores/useBracketStore'
import { hasOfficialFinalResult } from '../../lib/bracketCompletion'
import { compareDivisions } from '../../lib/divisionOrder'
import BackLink from '../BackLink'
import { Badge } from '../ui/badge'
import { Button } from '../ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu'

function getStatusInfo(status, { usesRoundLocks = false, roundStates = null, resultsFinal = false } = {}) {
  // Ticket ea99a2fa: once the championship has an official result the bracket
  // is over. The server flips status to 'complete' at that point; resultsFinal
  // is the same fact read off the loaded picks, so a page that loaded before
  // the flip doesn't keep reading "Open for Picks".
  if (status === 'complete' || (status !== 'draft' && resultsFinal)) {
    return { label: 'Complete', variant: 'muted' }
  }

  // Multi-week (round-locked) brackets keep their bracket-level status for the
  // whole run, in BOTH directions: it stays 'open' between weekends so entries
  // can still be created, and it can sit at a stale 'locked' from before the
  // admin opted into per-round locking. Either way the raw status misrepresents
  // what the user can do — the MLP Playoffs bracket read "Open for Picks" in
  // this header while every round was locked and the banner below it said "All
  // rounds are locked" (tickets 73266ee6 / 29b69ed7). The real state lives per
  // round, so derive the badge from the rounds and ignore the bracket status
  // entirely; only 'draft' and 'complete' are still bracket-level facts.
  if (usesRoundLocks && status !== 'draft' && roundStates) {
    const rounds = Object.values(roundStates)
    if (rounds.some(r => r.status === 'open')) return { label: 'Open for Picks', variant: 'teal' }
    if (rounds.some(r => r.status === 'locked')) return { label: 'Locked', variant: 'blue' }
    // Every round still pending — matchups aren't announced yet. Same wording
    // and colour the admin sees for that round on /brackets/:id/rounds.
    return { label: 'Not Open', variant: 'orange' }
  }

  switch (status) {
    case 'draft':
      return { label: 'Draft', variant: 'orange' }
    case 'open':
      return { label: 'Open for Picks', variant: 'teal' }
    case 'locked':
      return { label: 'Locked', variant: 'blue' }
    default:
      return { label: status || 'Unknown', variant: 'muted' }
  }
}

export default function BracketHeader() {
  const tournament = useBracketStore(s => s.tournament)
  const bracket = useBracketStore(s => s.bracket)
  const bracketId = useBracketStore(s => s.bracketId)
  const tournamentBrackets = useBracketStore(s => s.tournamentBrackets)
  const isOwnEntry = useBracketStore(s => s.isOwnEntry)
  const isViewingEntry = useBracketStore(s => s.isViewingEntry)
  const viewingLeaderboard = useBracketStore(s => s.viewingLeaderboard)
  const isAdminBracketContext = useBracketStore(s => s.isAdminBracketContext)
  const entryOwnerFirstName = useBracketStore(s => s.entryOwnerFirstName)
  const ownedGroups = useBracketStore(s => s.ownedGroups)
  const usesRoundLocks = useBracketStore(s => s.usesRoundLocks)
  const roundStates = useBracketStore(s => s.roundStates)
  const matches = useBracketStore(s => s.matches)
  const picks = useBracketStore(s => s.picks)
  const navigate = useBracketStore(s => s._navigate)
  const handleDivisionSwitch = useBracketStore(s => s.handleDivisionSwitch)
  const handleToggleBracketInGroup = useBracketStore(s => s.handleToggleBracketInGroup)

  const statusInfo = getStatusInfo(bracket?.status, {
    usesRoundLocks,
    roundStates,
    resultsFinal: hasOfficialFinalResult(matches, picks),
  })
  const headerRef = useRef(null)

  // Expose the header's live height as --bracket-header-offset so sibling sticky
  // elements (the round-labels bar) can sit flush below without hard-coding px.
  useEffect(() => {
    const el = headerRef.current
    if (!el) return
    const update = () => {
      const h = el.getBoundingClientRect().height
      document.documentElement.style.setProperty('--bracket-header-offset', `${Math.round(h)}px`)
    }
    update()
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(update) : null
    ro?.observe(el)
    return () => {
      ro?.disconnect()
      document.documentElement.style.removeProperty('--bracket-header-offset')
    }
  }, [])

  return (
    <div
      ref={headerRef}
      data-testid="bracket-header"
      className="bracket-header-bar sticky top-0 z-40 bg-background/95 backdrop-blur-md border-b border-border/40"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center gap-2 py-2 flex-wrap sm:flex-nowrap">
          {/* The back arrow and the tournament-name breadcrumb go to the SAME
              place (owner 2026-06-09: "in my mind they should be the same
              link"). Admins who entered via an admin entry point (mode=
              editOfficial / adminView) back out to /admin/* ; everyone else
              goes to the tournament page deterministically — NOT history-back,
              which could dump you on a stale/empty bracket (the reported bug).
              Falls back to history -1 only before the tournament id is known. */}
          <BackLink
            to={
              isAdminBracketContext && tournament?.id
                ? `/admin/tournaments/${tournament.id}/edit`
                : tournament?.id
                  ? `/tournaments/${tournament.id}`
                  : -1
            }
          />

          <button
            type="button"
            onClick={() => {
              if (!tournament?.id) return
              // Tournament breadcrumb walks back into admin whenever the
              // admin entered via an admin entry point (edit-official OR
              // view-bracket from the seed editor).
              navigate(
                isAdminBracketContext
                  ? `/admin/tournaments/${tournament.id}/edit`
                  : `/tournaments/${tournament.id}`
              )
            }}
            className="text-sm text-muted-foreground hover:text-primary transition-colors font-medium cursor-pointer text-left truncate"
          >
            {tournament?.name || 'Loading...'}
          </button>

          <span className="text-muted-foreground shrink-0 mx-1">/</span>

          <div className="flex items-center gap-2 min-w-0">
            {tournamentBrackets.length > 1 ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="flex items-center gap-1 text-sm font-medium hover:text-primary transition-colors cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded-sm truncate">
                    {bracket?.divisionName || (bracket?.format === 'pool_then_sunday' ? 'Pool Play' : 'Loading...')}
                    <ChevronDown className="h-4 w-4 opacity-50 shrink-0" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start">
                  {[...tournamentBrackets].sort(compareDivisions).map(b => (
                    <DropdownMenuItem
                      key={b.id}
                      onClick={() => handleDivisionSwitch(b.id)}
                      className="flex items-center justify-between"
                    >
                      {b.divisionName || (b.format === 'pool_then_sunday' ? 'Pool Play' : '')}
                      {b.id === bracketId && (
                        <Check className="h-4 w-4 text-primary ml-4" />
                      )}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <span className="text-sm font-medium truncate">{bracket?.divisionName || (bracket?.format === 'pool_then_sunday' ? 'Pool Play' : 'Loading...')}</span>
            )}

            {bracket?.status && (
              <Badge variant={statusInfo.variant} className="py-1 px-3">
                {statusInfo.label}
              </Badge>
            )}

            {/* The leaderboard tab keeps the loaded entry in the store so the
                bracket is still there when you switch back — but you are no
                longer looking at those picks, so the breadcrumb badge must go
                away the moment the Leaderboard tab is selected (ticket
                b7285cfd: "Holly's picks" stuck in the breadcrumb after
                clicking the Leaderboard icon to go find another player). */}
            {isViewingEntry && !isOwnEntry && !viewingLeaderboard && entryOwnerFirstName && (
              <Badge variant="purple" className="py-1 px-3 whitespace-nowrap">
                Viewing {entryOwnerFirstName}'s picks
              </Badge>
            )}
          </div>

          {/* Actions — right-aligned */}
          <div className="flex items-center gap-2 ml-auto">
            {ownedGroups.length > 0 && isOwnEntry && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm">
                    <Users className="h-4 w-4 sm:mr-2" />
                    <span className="hidden sm:inline">Add to Group</span>
                    <ChevronDown className="h-4 w-4 ml-1 opacity-50" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>Your Groups</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {ownedGroups.map(group => (
                    <DropdownMenuItem
                      key={group.id}
                      onClick={() => handleToggleBracketInGroup(group.id, group.name, group.has_bracket)}
                      className="flex items-center justify-between"
                    >
                      <span>{group.name}</span>
                      {group.has_bracket ? (
                        <Check className="h-4 w-4 text-primary" />
                      ) : (
                        <Plus className="h-4 w-4 text-muted-foreground" />
                      )}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
